import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Loader2, Settings, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getFilteredSubNavItems, subNavItems } from './navigationConfig';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
// import UserProfileCard from './UserProfileCard';
import NavigationHeader from './navigation/NavigationHeader';
import SearchInput from './navigation/SearchInput';
import NavigationItem from './navigation/NavigationItem';
import { AppDispatch, RootState } from '@/store/store';
import { NavigationItem as NavItemType } from './types/navigation';
import { useInbox } from '@trycourier/react-hooks';
import { checkMessageNotifications, NavigationNotification } from '@/utils/navigationNotifications';
import { getPermissionMapping } from '@/constants/permissions';
import { getClerkPermissionForNavigation } from '@/utils/clerkPermissionMapper';
import { getUserInitials } from '@/utils/userUtils';
import { fetchAgentStatuses, getUserAgentStatus, selectAgentStatuses, selectIsLoading, selectUserCurrentStatus, updateUserAgentStatus } from '@/store/slices/agentStatuses/agentStatusesSlice';
import { useAuth } from '@/context/AuthContext';
import { UserProfileModal } from '../inbox/components/toolbar/UserProfileModal';
import { getPermissionForNavigation } from '@/utils/betterAuthPermissionMapper';
import Protect from '../auth/Protect';

interface SubNavigationProps {
  activeMainNav: string;
  isSecondPanelCollapsed: boolean;
  toggleSecondPanel: () => void;
  expandedItems: string[];
  toggleExpanded: (itemTitle: string) => void;
  navigate: (path: string) => void;
}
const SubNavigation = ({
  activeMainNav,
  isSecondPanelCollapsed,
  toggleSecondPanel,
  expandedItems,
  toggleExpanded,
  navigate
}: SubNavigationProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const {
    notifications
  } = useSelector((state: RootState) => state.notification);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  const [mentionsCount, setMentionsCount] = useState(0);
  // const {
  //   messages
  // } = useInbox();
  const [navNotifications, setNavNotifications] = useState<NavigationNotification[]>([]);
  const {
    sessionData: session,
  } = useAuth();

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [currentAgentStatus, setCurrentAgentStatus] = useState('Available');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const agentStatuses = useSelector(selectAgentStatuses);
  const userCurrentStatus = useSelector(selectUserCurrentStatus);
  const isLoading = useSelector(selectIsLoading);
  const user = session?.user;

  console.log("session", session);
  console.log("user", user);

  useEffect(() => {
    dispatch(getUserAgentStatus());
    dispatch(fetchAgentStatuses());
  }, [dispatch]);

  useEffect(() => {
    if (userCurrentStatus) {
      const status = agentStatuses.find(status => status.id === userCurrentStatus);
      if (status) {
        setCurrentAgentStatus(status.name);
        setSelectedStatus(status.id);
      }
    }
  }, [userCurrentStatus, agentStatuses]);

  // Get role from Clerk organization membership only and strip org: prefix
  const rawOrganizationRole = user?.organizationRole;
  const organizationRole = rawOrganizationRole?.replace(/^(org:|ORG:)/i, '') || null;
  const userRole = organizationRole || 'agent';

  // Debug logging
  // console.log('Raw organization role:', rawOrganizationRole);
  // console.log('Processed organization role:', organizationRole);
  // console.log('Final role:', userRole);
  // Use mock statuses if Redux store is empty
  const displayStatuses = agentStatuses.length > 0 ? agentStatuses : [];
  // useEffect(() => {
  //   setNavNotifications(checkMessageNotifications(messages));
  // }, [messages]);

  useEffect(() => {
    if (navNotifications.length > 0) {
      const currentPath = location.pathname;
      setNavNotifications(prev => prev.map(notif => ({
        ...notif,
        viewed: notif.viewed || notif.navPath === currentPath
      })));
    }
  }, [location.pathname, navNotifications.length]);

  const hasUnviewedNotification = (path: string) => {
    return navNotifications.some(notif => notif.navPath === path && !notif.viewed);
  };

  useEffect(() => {
    setUnassignedCount(notifications?.unassigned?.length ?? 0);
    setInboxCount(notifications?.inbox?.length ?? 0);
    setMentionsCount(notifications?.mentions?.length ?? 0);
  }, [notifications]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === '/') {
        const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
        if (searchInput) {
          event.preventDefault();
          searchInput.focus();
        }
      }
      if (event.ctrlKey && event.key === '[') {
        toggleSecondPanel();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [toggleSecondPanel]);

  const isItemActive = (path?: string) => path && location.pathname === path;
  const hasActiveChild = (children?: NavItemType[]) => children?.some(child => isItemActive(child.path));

  // Memoize filter function
  const filterMenuItems = useMemo(() => {
    return (items: any[]): any[] => {
      return items.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);
          item.children = filteredChildren;
          return matchesSearch || filteredChildren.length > 0;
        }
        return matchesSearch;
      });
    };
  }, [searchQuery]);

  const handleStatusChange = (statusId: string) => {
    dispatch(updateUserAgentStatus({ agentStatusId: statusId }));
  }

  return (
    <div className={cn(
      `${isSecondPanelCollapsed ? 'w-12' : 'w-64'} min-h-screen relative flex flex-col transition-all duration-300 ease-in-out overflow-hidden`
    )}>
      {/* Clean gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50/95 via-white/90 to-slate-100/85" />
      <div className="absolute inset-0 bg-gradient-to-b from-purple-500/3 via-transparent to-indigo-500/3" />

      {/* Subtle border */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-slate-200/60 via-slate-300/40 to-slate-200/60" />

      {/* Content with clean layout */}
      <div className="flex-1 flex flex-col relative z-10">
        {/* Header section - clean and minimal */}
        <div className="px-5 pt-5 pb-3">
          <NavigationHeader activeMainNav={activeMainNav} isSecondPanelCollapsed={isSecondPanelCollapsed} toggleSecondPanel={toggleSecondPanel} />
        </div>

        {/* Search section - seamless integration */}
        {!isSecondPanelCollapsed && (
          <div className="px-5 pb-4">
            <SearchInput
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        )}

        {/* Navigation content - clean scrollable area */}
        <div className="flex-1 px-3">
          <ScrollArea className="h-full">
            <div className="space-y-1 py-2">
              {getFilteredSubNavItems(activeMainNav).map((item, index) => (
                <div key={item.title} className="relative">
                  <Protect permission={getPermissionForNavigation(item.permissionKey)} fallback={null} >
                    <NavigationItem
                      item={item}
                      badge={item.title === 'Unassigned' ? unassignedCount : item.title === 'Your Inbox' ? inboxCount : item.title === 'Mentions' ? mentionsCount : 0}
                      isSecondPanelCollapsed={isSecondPanelCollapsed}
                      expandedItems={expandedItems}
                      toggleExpanded={toggleExpanded}
                      hasActiveChild={hasActiveChild}
                      isItemActive={isItemActive}
                      navigate={navigate}
                      filterMenuItems={filterMenuItems}
                      hasNotification={hasUnviewedNotification(item.path)}
                    />
                  </Protect>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* User profile section - enhanced container with consistent design */}
      <div className="relative border-t border-slate-200/50">
        {/* Enhanced background to match the main area */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50/80 via-white/70 to-slate-100/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/2 to-indigo-500/3" />

        {/* Enhanced Unified User Profile Section */}
        <div className={cn("relative z-10 transition-all duration-300", isSecondPanelCollapsed ? "p-3" : "p-4")}>
          <div className={cn("bg-gradient-to-br from-background via-background/95 to-muted/30 border border-border/60 shadow-sm backdrop-blur-sm transition-all duration-300", isSecondPanelCollapsed ? "rounded-lg p-1" : "rounded-xl")}>
            {/* Main Profile Content */}
            {isSecondPanelCollapsed ? (
              /* Collapsed State - Compact Avatar with Popover */
              <div className="flex justify-center p-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <div className="relative group cursor-pointer p-1.5 rounded-lg hover:bg-muted/40 transition-all duration-200">
                      <Avatar className="ring-2 ring-primary/20 ring-offset-1 ring-offset-background transition-all duration-300 h-8 w-8 group-hover:ring-primary/40">
                        <AvatarImage src={user?.imageUrl} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-semibold text-xs">
                          {getUserInitials(user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.emailAddresses?.[0]?.emailAddress || 'User')}
                        </AvatarFallback>
                      </Avatar>

                      {/* Enhanced Status Indicator */}
                      <div className={cn("absolute -bottom-0 -right-0 border-2 border-background rounded-full shadow-lg transition-all duration-300 h-3 w-3 animate-pulse", currentAgentStatus === 'Available' && "bg-green-500", currentAgentStatus === 'Busy' && "bg-red-500", currentAgentStatus === 'Away' && "bg-yellow-500", currentAgentStatus === 'Break' && "bg-orange-500", currentAgentStatus === 'Training' && "bg-blue-500", currentAgentStatus === 'Meeting' && "bg-purple-500", currentAgentStatus === 'Do Not Disturb' && "bg-red-600", currentAgentStatus === 'Offline' && "bg-gray-500")} />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[320px] p-0 border-0 shadow-2xl bg-background/80 backdrop-blur-xl rounded-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
                    align="end"
                    side="right"
                    sideOffset={12}
                  >
                    {/* Modern Glass Card Design */}
                    <div className="relative overflow-hidden">
                      {/* Sleek Background with Subtle Patterns */}
                      <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-background/90" />
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl transform translate-x-8 -translate-y-8" />

                      {/* Content Layer */}
                      <div className="relative">
                        {/* Header Section - Compact and Modern */}
                        <div className="p-5 pb-4">
                          <div className="flex items-center gap-3">
                            {/* Avatar with Modern Styling */}
                            <div className="relative">
                              <Avatar className="h-12 w-12 ring-2 ring-primary/30 ring-offset-2 ring-offset-background/80 shadow-lg border border-border/20">
                                <AvatarImage src={user?.imageUrl} className="object-cover" />
                                <AvatarFallback className="bg-gradient-to-br from-primary via-primary/90 to-primary/70 text-primary-foreground font-bold text-sm">
                                  {getUserInitials(user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.emailAddresses?.[0]?.emailAddress || 'User')}
                                </AvatarFallback>
                              </Avatar>

                              {/* Modern Status Indicator */}
                              <div className={cn(
                                "absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-background/90 shadow-lg flex items-center justify-center transition-all duration-300",
                                currentAgentStatus === 'Available' && "bg-emerald-500 shadow-emerald-500/30",
                                currentAgentStatus === 'Busy' && "bg-rose-500 shadow-rose-500/30",
                                currentAgentStatus === 'Away' && "bg-amber-500 shadow-amber-500/30",
                                currentAgentStatus === 'Break' && "bg-orange-500 shadow-orange-500/30",
                                currentAgentStatus === 'Training' && "bg-blue-500 shadow-blue-500/30",
                                currentAgentStatus === 'Meeting' && "bg-purple-500 shadow-purple-500/30",
                                currentAgentStatus === 'Do Not Disturb' && "bg-red-600 shadow-red-600/30",
                                currentAgentStatus === 'Offline' && "bg-slate-400 shadow-slate-400/30"
                              )}>
                                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                              </div>
                            </div>

                            {/* User Info - Compact */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-sm text-foreground leading-tight truncate">
                                {user?.fName && user?.lName ? `${user.fName} ${user.lName}` : user?.email?.split('@')[0] || 'User'}
                              </h3>
                              <p className="text-xs text-muted-foreground/80 truncate mt-0.5">
                                {user?.email}
                              </p>
                            </div>
                          </div>

                          {/* Role and Status Badges - Modern Pills */}
                          <div className="flex items-center gap-2 mt-3">
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/20 rounded-full backdrop-blur-sm">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary/80" />
                              <span className="text-xs font-medium text-primary">
                                {userRole || "Agent"}
                              </span>
                            </div>

                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-muted/60 border border-border/30 rounded-full backdrop-blur-sm">
                              <div className={cn(
                                "w-1.5 h-1.5 rounded-full",
                                currentAgentStatus === 'Available' && "bg-emerald-500",
                                currentAgentStatus === 'Busy' && "bg-rose-500",
                                currentAgentStatus === 'Away' && "bg-amber-500",
                                currentAgentStatus === 'Break' && "bg-orange-500",
                                currentAgentStatus === 'Training' && "bg-blue-500",
                                currentAgentStatus === 'Meeting' && "bg-purple-500",
                                currentAgentStatus === 'Do Not Disturb' && "bg-red-600",
                                currentAgentStatus === 'Offline' && "bg-slate-400"
                              )} />
                              <span className="text-xs font-medium text-muted-foreground">
                                {currentAgentStatus}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Modern Divider */}
                        <div className="h-px bg-gradient-to-r from-transparent via-border/60 to-transparent mx-5" />

                        {/* Actions Section - Modern and Clean */}
                        <div className="p-5 pt-4 space-y-3">
                          {/* Manage Account Button - Modern Design */}
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-10 px-3 text-sm hover:bg-primary/10 hover:text-primary transition-all duration-300 group rounded-xl border border-transparent hover:border-primary/20 hover:shadow-lg hover:shadow-primary/10"
                            onClick={() => setIsProfileModalOpen(true)}
                          >
                            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-all duration-300 mr-3">
                              <User className="h-3.5 w-3.5 text-primary" />
                            </div>
                            <span className="font-medium">Manage Account</span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0.5" />
                          </Button>

                          {/* Status Selector - Modern Dropdown */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 px-1">
                              <div className="h-3 w-0.5 bg-gradient-to-b from-primary/70 to-primary/30 rounded-full" />
                              <span className="text-xs font-semibold text-foreground/70 uppercase tracking-wide">
                                Status
                              </span>
                            </div>

                            {isLoading ? (
                              <div className="flex items-center justify-center h-full">
                                <Loader2 className="h-4 w-4 animate-spin" />
                              </div>
                            ) : (
                              <Select value={selectedStatus} onValueChange={handleStatusChange}>
                                <SelectTrigger className="w-full h-10 text-sm bg-background/60 border border-border/40 hover:border-primary/40 hover:bg-background/80 transition-all duration-300 rounded-lg shadow-sm backdrop-blur-sm">
                                  <div className="flex items-center gap-2.5">
                                    <div
                                      className="w-2.5 h-2.5 rounded-full shadow-sm border border-white/20"
                                      style={{ backgroundColor: displayStatuses.find(s => s.id === selectedStatus)?.color || '#10b981' }}
                                    />
                                    <SelectValue placeholder="Change status" />
                                  </div>
                                </SelectTrigger>

                                <SelectContent className="min-w-[280px] border border-border/40 shadow-xl rounded-xl bg-background/95 backdrop-blur-md p-1">
                                  {displayStatuses.map(status => (
                                    <SelectItem
                                      key={status.id}
                                      value={status.id}
                                      className="cursor-pointer p-3 m-0.5 rounded-lg focus:bg-primary/8 hover:bg-primary/5 transition-all duration-200 group border-0"
                                    >
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div className="flex items-center gap-3 w-full">
                                              <div className="relative">
                                                <div
                                                  className="w-3 h-3 rounded-full shadow-sm border border-white/10 transition-transform group-hover:scale-110"
                                                  style={{ backgroundColor: status.color }}
                                                />
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                  <span className="text-sm font-medium text-foreground truncate">
                                                    {status.name}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="left" className="max-w-xs bg-background/95 backdrop-blur-sm border border-border/50 rounded-lg">
                                            <p className="text-xs font-medium">{status.description}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              /* Expanded State - Compact Horizontal Layout */
              <div className="flex items-center gap-3 p-3">
                {/* Compact Avatar with Status */}
                <div className="relative flex-shrink-0">
                  <Avatar className="ring-2 ring-primary/20 ring-offset-1 ring-offset-background transition-all duration-300 h-9 w-9">
                    <AvatarImage
                      src={user?.image}
                      alt={user?.name || user?.email?.split('@')[0] || 'User'}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-semibold text-xs">
                      {getUserInitials(user?.name || user?.email?.split('@')[0] || 'User')}
                    </AvatarFallback>
                  </Avatar>

                  {/* Status Indicator */}
                  <div className={cn(
                    "absolute -bottom-0.5 -right-0.5 border-2 border-background rounded-full shadow-md h-2.5 w-2.5",
                    currentAgentStatus === 'Available' && "bg-green-500",
                    currentAgentStatus === 'Busy' && "bg-red-500",
                    currentAgentStatus === 'Away' && "bg-yellow-500",
                    currentAgentStatus === 'Break' && "bg-orange-500",
                    currentAgentStatus === 'Training' && "bg-blue-500",
                    currentAgentStatus === 'Meeting' && "bg-purple-500",
                    currentAgentStatus === 'Do Not Disturb' && "bg-red-600",
                    currentAgentStatus === 'Offline' && "bg-gray-500"
                  )} />
                </div>

                {/* User Info & Controls - Compact Layout */}
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  {/* User name */}
                  <div className="text-xs font-semibold text-foreground truncate">
                    {user?.name || user?.email?.split('@')[0] || 'User'}
                  </div>

                  {
                    isLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    ) : (
                      < Select value={selectedStatus} onValueChange={handleStatusChange}>
                        <SelectTrigger className="h-6 text-xs border border-border/40 bg-background/50 backdrop-blur-sm px-2 hover:bg-accent/50 transition-all duration-200 rounded-md focus:ring-1 focus:ring-ring/50 focus:border-ring">
                          <SelectValue className="text-foreground font-medium" />
                        </SelectTrigger>
                        <SelectContent className="min-w-[160px] p-1">
                          <div className="mb-1 px-2 py-0.5">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</p>
                          </div>
                          {displayStatuses.map(status => (
                            <TooltipProvider key={status.id}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SelectItem
                                    value={status.id}
                                    className="px-2 py-1 cursor-pointer hover:bg-accent/60 focus:bg-accent transition-colors rounded-sm data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary"
                                  >
                                    <div className="flex items-center gap-2 w-full">
                                      <div
                                        className="w-2 h-2 rounded-full shadow-sm"
                                        style={{ backgroundColor: status.color }}
                                      />
                                      <span className="text-xs font-medium truncate flex-1">{status.name}</span>
                                    </div>
                                  </SelectItem>
                                </TooltipTrigger>
                                {status.description && (
                                  <TooltipContent side="right" className="max-w-xs">
                                    <p className="text-xs">{status.description}</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          ))}
                        </SelectContent>
                      </Select>
                    )
                  }
                </div>

                {/* Settings Icon - Compact */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 hover:bg-muted/60 transition-all duration-200 hover:scale-105"
                  onClick={() => setIsProfileModalOpen(true)}
                >
                  <Settings className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced UserProfile Modal */}
        <Dialog open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen}>
          <DialogContent className="max-w-fit w-auto h-auto p-0 overflow-hidden border-0 shadow-none bg-transparent">
            {/* <UserProfile appearance={{
              elements: {
                rootBox: 'w-auto h-auto',
                card: 'w-auto h-auto shadow-none border-0 bg-background rounded-lg'
              }
            }} /> */}
            <UserProfileModal open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen} />
          </DialogContent>
        </Dialog>
      </div>
    </div >
  );
};
export default SubNavigation;